import { Lock, Plus, X } from 'lucide-react';
import type { OpenApiSchema } from '@/api/client';
import {
  setBodyField,
  addCustomBodyField,
  updateCustomBodyField,
  removeCustomBodyField,
  useTryIt,
} from '@/store/tryIt';
import { cn } from '@/lib/cn';

/**
 * Type-aware form-field renderer for a JSON body schema.
 *
 * Rather than asking the user to write raw JSON, we render one input per
 * property — picking input type based on schema:
 *
 *   string + enum         → <select>
 *   string + format=date  → <input type="date">
 *   string default        → <input type="text">
 *   integer / number      → <input type="number">
 *   boolean               → checkbox
 *   array of primitives   → textarea (one item per line)
 *   nested object         → recursive render (collapsed for now: raw subform)
 *
 * Pas submit, TryItForm.serializeBodyFromFields() yang convert state ke JSON.
 *
 * Auto-locked fields:
 *   Untuk field yang namanya match `lockedFields` (mis. secret_key, api_key),
 *   nilainya udah pre-set dari session di TryItForm. Di sini kita tampilin
 *   sebagai disabled input dengan ikon gembok + label "from session" — jadi
 *   user tahu kenapa gak bisa di-edit.
 */
export interface BodyFieldsFormProps {
  schema: OpenApiSchema | null;
  values: Record<string, unknown>;
  /** Field names yang di-lock (auto-filled from session, gak boleh di-edit). */
  lockedFields?: string[];
  /** Allow user to add arbitrary key/value rows beyond the schema. */
  allowCustomFields?: boolean;
}

const REQUIRED_CLASS = 'text-error';

/**
 * Body editor — renders one typed input per schema property, plus optionally
 * a list of custom rows ([name][value][remove]) untuk endpoint yang gak
 * punya documented schema. "Add field" button di bawah.
 *
 * Bila schema empty + allowCustomFields=false → kasih tip kecil + tombol Add
 * tetap muncul (better than dead-end raw JSON textarea).
 */
export function BodyFieldsForm({
  schema,
  values,
  lockedFields = [],
  allowCustomFields = true,
}: BodyFieldsFormProps) {
  const properties = schema?.properties || {};
  const required = new Set(schema?.required || []);
  const propertyKeys = Object.keys(properties);
  const customFields = useTryIt().customBodyFields;

  const hasSchemaFields = propertyKeys.length > 0;
  const hasCustomFields = customFields.length > 0;

  return (
    <div className="space-y-3">
      {hasSchemaFields &&
        Object.entries(properties).map(([name, fieldSchema]) => {
          const isRequired = required.has(name);
          const isLocked = lockedFields.includes(name);
          return (
            <FieldRow
              key={name}
              name={name}
              schema={fieldSchema}
              value={values[name]}
              required={isRequired}
              locked={isLocked}
              onChange={(v) => setBodyField(name, v)}
            />
          );
        })}

      {!hasSchemaFields && !hasCustomFields && allowCustomFields && (
        <p className="text-2xs text-fg-tertiary">
          Endpoint ini gak ada documented schema. Tambah field di bawah, atau kirim request
          kosong dulu — backend biasanya jawab dengan list field yg dibutuhkan.
        </p>
      )}

      {/* Custom rows — user-added beyond schema */}
      {customFields.map((row) => (
        <CustomFieldRow key={row.id} row={row} />
      ))}

      {allowCustomFields && (
        <button
          type="button"
          onClick={addCustomBodyField}
          className="inline-flex items-center gap-1.5 px-3 h-8 rounded-pill border-2 border-border-strong bg-base shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-brutal-none transition-all text-2xs font-display font-bold"
        >
          <Plus size={12} />
          Add field
        </button>
      )}
    </div>
  );
}

/**
 * One user-added custom row. Local string state untuk responsiveness; sync
 * ke store on every keystroke (store-driven render kalau ada race).
 */
function CustomFieldRow({ row }: { row: { id: string; name: string; value: string } }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        value={row.name}
        onChange={(e) => updateCustomBodyField(row.id, { name: e.target.value })}
        placeholder="field_name"
        className="w-36 h-9 px-3 rounded-pill border-2 border-border-strong bg-base font-mono text-2xs font-bold text-fg placeholder:text-fg-tertiary placeholder:font-normal focus:outline-none focus:shadow-brutal-sm transition-shadow shrink-0"
      />
      <input
        type="text"
        value={row.value}
        onChange={(e) => updateCustomBodyField(row.id, { value: e.target.value })}
        placeholder="value"
        className="flex-1 h-9 px-3 rounded-pill border-2 border-border-strong bg-base font-mono text-2xs text-fg placeholder:text-fg-tertiary focus:outline-none focus:shadow-brutal-sm transition-shadow"
      />
      <button
        type="button"
        onClick={() => removeCustomBodyField(row.id)}
        title="Remove field"
        className="w-8 h-8 rounded-pill border-2 border-border-strong bg-method-delete shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-brutal-none transition-all flex items-center justify-center shrink-0"
      >
        <X size={12} />
      </button>
    </div>
  );
}

interface FieldRowProps {
  name: string;
  schema: OpenApiSchema;
  value: unknown;
  required: boolean;
  locked: boolean;
  onChange: (value: unknown) => void;
}

function FieldRow({ name, schema, value, required, locked, onChange }: FieldRowProps) {
  const labelId = `tryit-field-${name}`;
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <label
          htmlFor={labelId}
          className="text-2xs font-mono text-fg flex items-center gap-1"
        >
          {name}
          {required && <span className={REQUIRED_CLASS}>*</span>}
        </label>
        {locked && (
          <span
            className="inline-flex items-center gap-0.5 text-2xs text-fg-tertiary"
            title="Auto-filled from your current session — gak perlu di-isi"
          >
            <Lock size={9} />
            from session
          </span>
        )}
      </div>

      <FieldInput
        id={labelId}
        schema={schema}
        value={value}
        locked={locked}
        onChange={onChange}
      />

      {schema.description && (
        <p className="mt-0.5 text-2xs text-fg-tertiary leading-tight">{schema.description}</p>
      )}
    </div>
  );
}

interface FieldInputProps {
  id: string;
  schema: OpenApiSchema;
  value: unknown;
  locked: boolean;
  onChange: (value: unknown) => void;
}

function FieldInput({ id, schema, value, locked, onChange }: FieldInputProps) {
  const baseInput = cn(
    'w-full h-9 px-3 rounded-pill border-2 bg-base font-mono text-2xs text-fg',
    'placeholder:text-fg-tertiary focus:outline-none focus:shadow-brutal-sm transition-shadow',
    locked ? 'border-border-subtle opacity-60 cursor-not-allowed' : 'border-border-strong'
  );

  const placeholder = String(schema.example ?? '');

  // 1. Enum → <select>
  if (schema.enum && schema.enum.length > 0) {
    return (
      <select
        id={id}
        disabled={locked}
        value={value === undefined || value === null ? '' : String(value)}
        onChange={(e) => onChange(e.target.value)}
        className={baseInput}
      >
        <option value="" disabled>
          (pilih salah satu)
        </option>
        {schema.enum.map((opt) => (
          <option key={String(opt)} value={String(opt)}>
            {String(opt)}
          </option>
        ))}
      </select>
    );
  }

  // 2. Boolean → checkbox + label
  if (schema.type === 'boolean') {
    return (
      <div className="flex items-center gap-2 h-7">
        <input
          id={id}
          type="checkbox"
          disabled={locked}
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          className="h-3.5 w-3.5 rounded border-border bg-base/50 text-accent focus:ring-accent disabled:opacity-60"
        />
        <span className="text-2xs text-fg-tertiary">{value ? 'true' : 'false'}</span>
      </div>
    );
  }

  // 3. Number → number input
  if (schema.type === 'integer' || schema.type === 'number') {
    return (
      <input
        id={id}
        type="number"
        step={schema.type === 'integer' ? '1' : 'any'}
        disabled={locked}
        value={value === undefined || value === null || value === '' ? '' : String(value)}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '') return onChange(undefined);
          const num = Number(v);
          onChange(Number.isNaN(num) ? v : num);
        }}
        placeholder={placeholder}
        className={baseInput}
      />
    );
  }

  // 4. Array of primitives → textarea (one item per line)
  if (schema.type === 'array' && schema.items && (schema.items.type === 'string' || schema.items.type === 'number' || schema.items.type === 'integer' || schema.items.type === 'boolean')) {
    const lines = Array.isArray(value)
      ? (value as unknown[]).map(String).join('\n')
      : typeof value === 'string'
      ? value
      : '';
    return (
      <textarea
        id={id}
        disabled={locked}
        value={lines}
        onChange={(e) => {
          const items = e.target.value.split('\n').map((s) => s.trim()).filter(Boolean);
          // Coerce ke tipe yg cocok untuk items
          const coerced = items.map((s) => {
            if (schema.items?.type === 'integer' || schema.items?.type === 'number') {
              const n = Number(s);
              return Number.isNaN(n) ? s : n;
            }
            if (schema.items?.type === 'boolean') return s === 'true';
            return s;
          });
          onChange(coerced);
        }}
        placeholder="(satu item per baris)"
        rows={3}
        className={cn(baseInput, 'h-auto py-1.5 resize-y')}
      />
    );
  }

  // 5. Object / array-of-objects / fallback unknown → raw JSON sub-input
  if (schema.type === 'object' || schema.type === 'array') {
    const json = value === undefined ? '' : JSON.stringify(value, null, 2);
    return (
      <textarea
        id={id}
        disabled={locked}
        value={json}
        onChange={(e) => {
          const txt = e.target.value;
          if (txt.trim() === '') return onChange(undefined);
          try {
            onChange(JSON.parse(txt));
          } catch {
            // Simpan as-string biar user gak kehilangan input, validate pas submit
            onChange(txt);
          }
        }}
        placeholder={schema.type === 'array' ? '[ ... ]' : '{ ... }'}
        rows={4}
        spellCheck={false}
        className={cn(baseInput, 'h-auto py-1.5 resize-y')}
      />
    );
  }

  // 6. Default: string text input
  return (
    <input
      id={id}
      type="text"
      disabled={locked}
      value={value === undefined || value === null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={baseInput}
    />
  );
}
